import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, 
  Send, 
  CheckCircle, 
  Users, 
  AlertCircle,
  Clock,
  Mail,
  School,
  UserCheck,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Trash2,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import apiService from '@/services/api';
import { getCurrentUser } from '@/data/users';
import { useNavigate } from 'react-router-dom';

export default function PublishReportsPage() {
  const currentUser = getCurrentUser();
  const navigate = useNavigate();
  
  // Check if user is principal or admin - only they can access this page
  const isPrincipal = currentUser?.role === 'principal';
  const isAdmin = currentUser?.role === 'admin';
  const hasAccess = isPrincipal || isAdmin;
  
  // Detect if user is from a primary school (use school_type from login)
  const isPrimarySchool = currentUser?.school_type === 'primary' || 
                          currentUser?.school_id?.toLowerCase().includes('primary');
  
  const [selectedYear, setSelectedYear] = useState(isPrimarySchool ? '3' : '13');
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [loading, setLoading] = useState(false);
  const [loadingCompletion, setLoadingCompletion] = useState(false);
  const [publishedReports, setPublishedReports] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [showTeacherDetails, setShowTeacherDetails] = useState(false);
  const [showResetSection, setShowResetSection] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetOptions, setResetOptions] = useState({
    attendance: true,
    grades: true,
    publishStatus: true
  });
  const [completionData, setCompletionData] = useState({
    totalTeachers: 0,
    completedTeachers: 0,
    percentage: 0,
    teachers: []
  });
  const [stats, setStats] = useState({
    totalStudents: 0,
    completedGrades: 0,
    pendingGrades: 0
  });

  useEffect(() => {
    fetchPublishedReports();
    fetchSchoolName();
    // Set default year based on school type
    if (isPrimarySchool) {
      setSelectedYear('1');
    } else {
      setSelectedYear('13');
    }
  // eslint-disable-next-line
  }, [isPrimarySchool]);

  // Fetch grade completion when year or term changes
  useEffect(() => {
    if (selectedYear && selectedTerm) {
      fetchGradeCompletion();
    }
  // eslint-disable-next-line
  }, [selectedYear, selectedTerm]);

  const fetchGradeCompletion = async () => {
    setLoadingCompletion(true);
    try {
      const API_URL = '';
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      // Fetch classes for the selected year at this school
      const classesRes = await fetch(`${API_URL}/api/classes/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (classesRes.ok) {
        const allClasses = await classesRes.json();
        
        // Filter classes by selected year (for primary schools, filter by school too)
        let yearClasses = allClasses.filter(c => c.year === parseInt(selectedYear));
        
        // If primary school, also filter by school_id
        if (isPrimarySchool && currentUser?.school_id) {
          yearClasses = yearClasses.filter(c => c.school_id === currentUser.school_id);
        }
        
        // For each class, fetch students and check grade completion
        const teacherStatusPromises = yearClasses.map(async (cls) => {
          try {
            // Fetch students for this class
            const studentsRes = await fetch(`${API_URL}/api/classes/${cls.id}/students`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            let studentCount = 0;
            let gradesEntered = 0;
            
            if (studentsRes.ok) {
              const students = await studentsRes.json();
              studentCount = students.length;
              
              // Check actual grades from the grades API
              try {
                const gradesRes = await fetch(`${API_URL}/api/grades/class/${cls.id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (gradesRes.ok) {
                  const gradesData = await gradesRes.json();
                  // Count students who have at least one grade
                  if (typeof gradesData === 'object' && !Array.isArray(gradesData)) {
                    gradesEntered = Object.keys(gradesData).filter(sid => 
                      gradesData[sid] && gradesData[sid].length > 0
                    ).length;
                  }
                }
              } catch (e) {
                console.warn('Could not fetch grades for', cls.id);
              }
            }
            
            return {
              id: cls.teacher_id || cls.id,
              name: cls.teacher_name || `Year ${cls.year} Teacher`,
              className: cls.name || `Year ${cls.year}`,
              subject: cls.subject || 'General',
              completed: studentCount > 0 && gradesEntered >= studentCount * 0.5,
              studentCount,
              gradesEntered
            };
          } catch (e) {
            return {
              id: cls.id,
              name: `Year ${cls.year} Teacher`,
              className: cls.name || `Year ${cls.year}`,
              subject: cls.subject || 'General',
              completed: false,
              studentCount: 0,
              gradesEntered: 0
            };
          }
        });
        
        const teacherStatus = await Promise.all(teacherStatusPromises);
        
        const completedCount = teacherStatus.filter(t => t.completed).length;
        const percentage = teacherStatus.length > 0 
          ? Math.round((completedCount / teacherStatus.length) * 100) 
          : 0;
        
        // Calculate total students
        const totalStudents = teacherStatus.reduce((sum, t) => sum + t.studentCount, 0);
        const totalGradesEntered = teacherStatus.reduce((sum, t) => sum + t.gradesEntered, 0);
        
        setCompletionData({
          totalTeachers: teacherStatus.length,
          completedTeachers: completedCount,
          percentage,
          teachers: teacherStatus
        });
        
        setStats({
          totalStudents,
          completedGrades: totalGradesEntered,
          pendingGrades: totalStudents - totalGradesEntered
        });
      }
    } catch (error) {
      console.error('Error fetching grade completion:', error);
    } finally {
      setLoadingCompletion(false);
    }
  };

  const fetchSchoolName = async () => {
    if (currentUser?.school_id) {
      try {
        const API_URL = '';
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_URL}/api/schools/${currentUser.school_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSchoolName(data.name || '');
        }
      } catch (error) {
        console.error('Error fetching school:', error);
      }
    }
  };

  const fetchPublishedReports = async () => {
    try {
      const API_URL = '';
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/admin/published-reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPublishedReports(data.published_reports || []);
      }
    } catch (error) {
      console.error('Error fetching published reports:', error);
      setPublishedReports([]);
    }
  };

  const handleNewTermReset = async () => {
    const selectedOptionsText = [];
    if (resetOptions.attendance) selectedOptionsText.push('Attendance records');
    if (resetOptions.grades) selectedOptionsText.push('Grade entries');
    if (resetOptions.publishStatus) selectedOptionsText.push('Report publish status');
    
    if (selectedOptionsText.length === 0) {
      toast.error('Please select at least one option to reset');
      return;
    }

    const confirmReset = window.confirm(
      `⚠️ NEW TERM RESET - ${selectedTerm}\n\n` +
      `You are about to reset the following for Year ${selectedYear}:\n` +
      `${selectedOptionsText.map(o => `• ${o}`).join('\n')}\n\n` +
      `This will clear all selected data for the previous term.\n\n` +
      `THIS ACTION CANNOT BE UNDONE!\n\n` +
      `Are you sure you want to proceed?`
    );

    if (!confirmReset) return;

    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      `FINAL CONFIRMATION\n\n` +
      `Type "RESET" mentally and click OK to confirm.\n\n` +
      `All ${selectedOptionsText.join(', ').toLowerCase()} for Year ${selectedYear} will be permanently deleted.`
    );

    if (!doubleConfirm) return;

    setResetLoading(true);
    try {
      const API_URL = '';
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/admin/reset-term`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          year: parseInt(selectedYear),
          term: selectedTerm,
          resetAttendance: resetOptions.attendance,
          resetGrades: resetOptions.grades,
          resetPublishStatus: resetOptions.publishStatus
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ Term reset complete! ${result.message || 'Data cleared successfully.'}`);
        setShowResetSection(false);
        fetchPublishedReports();
        fetchGradeCompletion();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset term data');
      }
    } catch (error) {
      console.error('Error resetting term:', error);
      toast.error('Failed to reset term data. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePublishReport = async () => {
    console.log('🔵 handlePublishReport called', { selectedYear, selectedTerm });
    
    if (!selectedYear || !selectedTerm) {
      toast.error('Please select both year and term');
      return;
    }

    const confirmPublish = window.confirm(
      `Are you sure you want to publish ${selectedTerm} Year ${selectedYear} report cards?\n\n` +
      `This will:\n` +
      `- Make reports visible to all parents\n` +
      `- Send SMS notifications to parents\n` +
      `- Mark reports as official\n\n` +
      `This action cannot be undone.`
    );

    console.log('🔵 Confirmation result:', confirmPublish);
    if (!confirmPublish) return;

    setLoading(true);

    try {
      console.log('🔵 Calling API:', { year: parseInt(selectedYear), term: selectedTerm });
      
      // Call API to publish report
      const response = await apiService.publishReportCards(
        parseInt(selectedYear),
        selectedTerm
      );

      console.log('✅ API Success:', response);
      
      const smsInfo = response.sms_sent > 0 
        ? `${response.sms_sent} SMS sent` 
        : 'Running in test mode - no SMS sent';
      
      toast.success(
        `✅ ${selectedTerm} Year ${selectedYear} reports published! ${response.parent_count || 0} parents notified. ${smsInfo}`
      );

      // Refresh published reports list
      fetchPublishedReports();

    } catch (error) {
      console.error('❌ Error publishing reports:', error);
      toast.error('Failed to publish reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If not principal/admin, show access denied
  if (!hasAccess) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-6 w-6" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600 mb-4">
              Only <strong>Principals</strong> can access the Publish Reports page.
            </p>
            <p className="text-gray-600 mb-4">
              Teachers can view their grade completion status on the Grade Book page.
            </p>
            <Button onClick={() => navigate('/dashboard/menu')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <School className={`h-8 w-8 ${isPrimarySchool ? 'text-green-600' : 'text-blue-600'}`} />
          <h1 className="text-3xl font-bold">Publish Report Cards</h1>
        </div>
        <p className="text-gray-600">
          {schoolName ? `${schoolName} - ` : ''}Make report cards available to parents and send SMS notifications
        </p>
      </div>

      {/* Publish New Report Section */}
      <Card className="mb-6 shadow-lg">
        <CardHeader className={`bg-gradient-to-r ${isPrimarySchool ? 'from-green-600 to-emerald-600' : 'from-blue-600 to-indigo-600'} text-white`}>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Publish {isPrimarySchool ? 'Primary School' : ''} Report Cards
          </CardTitle>
          <CardDescription className={isPrimarySchool ? 'text-green-100' : 'text-blue-100'}>
            Select year and term to publish report cards to parents
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Year Selector - Different for Primary vs Secondary */}
            <div>
              <label className="text-sm font-medium mb-2 block">Year Level</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isPrimarySchool ? (
                    <>
                      <SelectItem value="1">Year 1</SelectItem>
                      <SelectItem value="2">Year 2</SelectItem>
                      <SelectItem value="3">Year 3</SelectItem>
                      <SelectItem value="4">Year 4</SelectItem>
                      <SelectItem value="5">Year 5</SelectItem>
                      <SelectItem value="6">Year 6</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="7">Year 7</SelectItem>
                      <SelectItem value="8">Year 8</SelectItem>
                      <SelectItem value="9">Year 9</SelectItem>
                      <SelectItem value="10">Year 10</SelectItem>
                      <SelectItem value="11">Year 11</SelectItem>
                      <SelectItem value="12">Year 12</SelectItem>
                      <SelectItem value="13">Year 13</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Term Selector */}
            <div>
              <label className="text-sm font-medium mb-2 block">Term</label>
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 1">Term 1</SelectItem>
                  <SelectItem value="Term 2">Term 2</SelectItem>
                  <SelectItem value="Term 3">Term 3</SelectItem>
                  <SelectItem value="Term 4">Term 4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Summary */}
            <div className="flex items-end">
              <div className="text-center">
                <div className={`text-3xl font-bold ${completionData.percentage === 100 ? 'text-green-600' : completionData.percentage >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {completionData.percentage}%
                </div>
                <div className="text-xs text-muted-foreground">Completion</div>
              </div>
            </div>
          </div>

          {/* Grade Completion Status */}
          <Card className={`mb-6 border-2 ${completionData.percentage === 100 ? 'border-green-200 bg-green-50' : completionData.percentage >= 70 ? 'border-yellow-200 bg-yellow-50' : 'border-red-200 bg-red-50'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  {completionData.percentage === 100 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                  Grade Completion Status - Year {selectedYear} ({selectedTerm})
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowTeacherDetails(!showTeacherDetails)}
                >
                  {showTeacherDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showTeacherDetails ? 'Hide Details' : 'Show Details'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCompletion ? (
                <div className="text-center py-4">Loading completion status...</div>
              ) : (
                <>
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>{completionData.completedTeachers} of {completionData.totalTeachers} teachers completed</span>
                      <span className="font-bold">{completionData.percentage}%</span>
                    </div>
                    <Progress 
                      value={completionData.percentage} 
                      className={`h-3 ${completionData.percentage === 100 ? '[&>div]:bg-green-600' : completionData.percentage >= 70 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'}`}
                    />
                  </div>

                  {/* Warning if not 100% */}
                  {completionData.percentage < 100 && (
                    <Alert className="mb-4 border-yellow-300 bg-yellow-50">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <AlertDescription className="text-yellow-800">
                        <strong>Warning:</strong> Not all teachers have completed their grades. 
                        Publishing now will send incomplete reports to parents.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Teacher Details Table */}
                  {showTeacherDetails && completionData.teachers.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Teacher / Class</TableHead>
                          <TableHead>{isPrimarySchool ? 'Subject' : 'Subject'}</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {completionData.teachers.map((teacher, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              <div>{teacher.name}</div>
                              <div className="text-xs text-muted-foreground">{teacher.className}</div>
                            </TableCell>
                            <TableCell>{teacher.subject}</TableCell>
                            <TableCell className="text-center">
                              {teacher.completed ? (
                                <Badge className="bg-green-100 text-green-800 border-green-300">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Complete
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800 border-red-300">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {completionData.teachers.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      No classes found for Year {selectedYear}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Publish Button */}
          <div className="flex items-center gap-4">
            <Button
              onClick={handlePublishReport}
              disabled={loading || completionData.percentage === 0}
              className={`flex-1 h-12 gap-2 ${completionData.percentage === 100 ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {completionData.percentage === 100 
                    ? 'Publish Reports' 
                    : `Publish Reports (${completionData.percentage}% Complete)`}
                </>
              )}
            </Button>
          </div>

          {/* Info Alert */}
          <Alert className="bg-blue-50 border-blue-200 mt-4">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>What happens when you publish:</strong>
              <ul className="list-disc ml-4 mt-2 space-y-1">
                <li>Report cards become visible in Parent Portal</li>
                <li>SMS notifications sent to all registered parents</li>
                <li>Parents can view and download reports</li>
                <li>Reports marked as &quot;Official&quot;</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* New Term Reset Section */}
      <Card className="mb-6 shadow-lg border-2 border-orange-200">
        <CardHeader 
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white cursor-pointer"
          onClick={() => setShowResetSection(!showResetSection)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              New Term Reset
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
              {showResetSection ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          <CardDescription className="text-orange-100">
            Clear previous term data to start fresh
          </CardDescription>
        </CardHeader>
        
        {showResetSection && (
          <CardContent className="pt-6">
            <Alert className="mb-6 border-red-300 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Warning:</strong> This action will permanently delete selected data for the chosen year and term. 
                Make sure all reports have been published and saved before resetting.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Year and Term Selection */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Select Year & Term to Reset
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Year Level</label>
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isPrimarySchool ? (
                          <>
                            <SelectItem value="1">Year 1</SelectItem>
                            <SelectItem value="2">Year 2</SelectItem>
                            <SelectItem value="3">Year 3</SelectItem>
                            <SelectItem value="4">Year 4</SelectItem>
                            <SelectItem value="5">Year 5</SelectItem>
                            <SelectItem value="6">Year 6</SelectItem>
                            <SelectItem value="all">All Years</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="7">Year 7</SelectItem>
                            <SelectItem value="8">Year 8</SelectItem>
                            <SelectItem value="9">Year 9</SelectItem>
                            <SelectItem value="10">Year 10</SelectItem>
                            <SelectItem value="11">Year 11</SelectItem>
                            <SelectItem value="12">Year 12</SelectItem>
                            <SelectItem value="13">Year 13</SelectItem>
                            <SelectItem value="all">All Years</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Term</label>
                    <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Term 1">Term 1</SelectItem>
                        <SelectItem value="Term 2">Term 2</SelectItem>
                        <SelectItem value="Term 3">Term 3</SelectItem>
                        <SelectItem value="Term 4">Term 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Reset Options */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  What to Reset
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input 
                      type="checkbox" 
                      checked={resetOptions.attendance}
                      onChange={(e) => setResetOptions({...resetOptions, attendance: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <div>
                      <div className="font-medium">Attendance Records</div>
                      <div className="text-sm text-muted-foreground">Daily roll call data</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input 
                      type="checkbox" 
                      checked={resetOptions.grades}
                      onChange={(e) => setResetOptions({...resetOptions, grades: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <div>
                      <div className="font-medium">Grade Entries</div>
                      <div className="text-sm text-muted-foreground">Assessment marks and scores</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input 
                      type="checkbox" 
                      checked={resetOptions.publishStatus}
                      onChange={(e) => setResetOptions({...resetOptions, publishStatus: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <div>
                      <div className="font-medium">Report Publish Status</div>
                      <div className="text-sm text-muted-foreground">Clear published report history</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Reset Button */}
            <Button
              onClick={handleNewTermReset}
              disabled={resetLoading || (!resetOptions.attendance && !resetOptions.grades && !resetOptions.publishStatus)}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              {resetLoading ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4" />
                  Reset Selected Data for {selectedTerm}
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Previously Published Reports */}
      <Card className="shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Published Reports History
          </CardTitle>
          <CardDescription>
            View all previously published report cards
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {publishedReports.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-2">No Reports Published Yet</p>
              <p className="text-sm text-gray-500">
                Published reports will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {publishedReports.map((report, index) => (
                <Card key={index} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">
                          {report.term} - Year {report.year}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Published: {new Date(report.published_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <Badge variant="outline" className="gap-1">
                            <Users className="h-3 w-3" />
                            {report.student_count} students
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            <Mail className="h-3 w-3 inline mr-1" />
                            SMS sent
                          </p>
                        </div>
                        <Badge className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Published
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
